import { AssignedConfigsDto, PaymentTypeField, ResidentEditParamsDTO, ResidentSortColumnEnum, ResidentSortDirectionEnum, Role } from '@inosio/payments-sdk';
import { Table, TableBody, TableCell, TableHead, TableRow, Card, Grid, Stack, Tooltip, Popover, Box, TableContainer, Typography, Divider, TableSortLabel } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useDispatch, useSelector } from 'react-redux';
import TablePagination from '@mui/material/TablePagination';
import NumberFormat from 'react-number-format';
import { selectResidentsHistory, selectResidentsCurrentPages, getEditResidentData, selectResidents } from '../residents/saga/pmResidentsSlice';
import RegisteredIcon from '../../icons/RegisteredIcon.svg';
import NotRegisteredIcon from '../../icons/NotRegisteredIcon.svg';
import EditResidentIcon from '../../icons/EditResidentIcon.svg';
import BlackAch from '../../icons/blackAch.svg';
import BlackRestrict from '../../icons/BlackDollar.svg';
import BlackCard from '../../icons/blackCard.svg';
import RedAch from '../../icons/redAch.svg';
import RedRestrict from '../../icons/RedDollar.svg';
import RedCard from '../../icons/redCard.svg';
import YellowRestrict from '../../icons/YellowDollar.svg';
import ResidentPortalIcon from '../../icons/ResidentPortal.svg';
import CircularProgress from '@mui/material/CircularProgress';
import MakeAdhocOneTimePayment from './MakeAdhocOtp';
import SpinnerWithNoConfirmation from '../../shared/Modal/spinnerWithNoConfirmation';
import BadRequestIcon from '../../icons/Badrequesterror.svg';
import Error500 from '../../icons/505Error.svg';
import BlackPartial from '../../icons/blackPartial.svg';
import RedPartial from '../../icons/redPartial.svg';
import { isActionAllowedInRange } from '../../resui/payments/components/UseSharedMethods';
import { getAllPropertyGroupsConfigs, selectPropertyGroupDetailsState } from '../../shared/sagas/propertyGroups/propertyGroupsSlice';
import { Fragment, useEffect, useState } from 'react';
import ResidentsTableHeader from './components/residentsTableHeader/ResidentsTableHeader';
import UserRegistrationModalManager, { UserRegistrationProfileDetails } from '../../shared/userRegistration/UserRegistrationModalManager';
import LogAsProfileUserModal from '../../shared/logas/LogAsProfileUsersModal';

export default function Residents() {
  const resident: any = {};
  const dispatch = useDispatch();
  const useStyles = makeStyles({
    gridContainer: {
      background: '#FFFFFF',
      borderRadius: '24px',
    },
    dialog: {
      padding: '20px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    buttontext: {
      fontFamily: 'sans-serif',
      fontStyle: 'normal',
      fontWeight: 500,
      fontSize: 'small',
    },
    dialogbox: {
      borderRadius: '20',
      borderStyle: 'solid',
    },
  });
  const classes = useStyles();

  //local states
  const [currentPage, setCurrentPage] = useState(0);
  const [rangeSize, setRangeSize] = useState(100);
  const [recordStartIndex, setRecordStartIndex] = useState(0);
  const [selectedResident, setSelectedResident] = useState(resident);
  const [showAdhocOTPDialog, setShowAdhocOTPDialog] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [selectedResidentUuid, setSelectedResidentUuid] = useState('');
  const open = Boolean(anchorEl);
  const [selectedSortingColumn, setSelectedSortingColumn] = useState<ResidentSortColumnEnum>(ResidentSortColumnEnum.Name);
  const [selectedSortingDirection, setSelectedSortingDirection] = useState<ResidentSortDirectionEnum>(ResidentSortDirectionEnum.Asc);
  const [openUserRegistrationModalManager, setOpenUserRegistrationModalManager] = useState(false);
  const [openLogAsProfileUsersModal, setOpenLogAsProfileUsersModal] = useState(false);

  //selectors
  const residentState = useSelector(selectResidents);
  const residentPages = useSelector(selectResidentsCurrentPages);
  const residentResponse = useSelector(selectResidentsHistory);
  const { allPropertyGroupsAssignedConfigs, isFetchingAllPropertyGroupConfigsFailed, isFetchingAllPropertyGroupConfigs, shouldRefreshAllPropertyGroupConfigs } =
    useSelector(selectPropertyGroupDetailsState);

  //useEffects
  useEffect(() => {
    if (shouldRefreshAllPropertyGroupConfigs) dispatch(getAllPropertyGroupsConfigs());
  }, []);

  //handlers
  const setSearchPageIndex = (pageNumber: number) => {
    if (!residentPages[pageNumber]) {
      const newIndex = pageNumber * rangeSize;
      setRecordStartIndex(newIndex);
    }
  };

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPageNumber: number) => {
    setSearchPageIndex(newPageNumber);
    setCurrentPage(newPageNumber);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setCurrentPage(0);
    setRecordStartIndex(0);
    setRangeSize(parseInt(event.target.value));
  };

  const handleOpenModal = (resident: any) => {
    setSelectedResident(resident);
  };

  const handleClick = (e: any, resUuid: string) => {
    setAnchorEl(e.currentTarget);
    setSelectedResidentUuid(resUuid);
  };
  const handleClose = () => setAnchorEl(null);

  // Handle sort
  const handleSort = (column: ResidentSortColumnEnum) => {
    if (selectedSortingColumn === column) {
      // If the same column is clicked, toggle the direction
      setSelectedSortingDirection((prevDirection) => (prevDirection === ResidentSortDirectionEnum.Asc ? ResidentSortDirectionEnum.Desc : ResidentSortDirectionEnum.Asc));
    } else {
      // If a new column is clicked, set it as the new sorting column and default to ascending
      setSelectedSortingColumn(column);
      setSelectedSortingDirection(ResidentSortDirectionEnum.Asc);
    }
    setRecordStartIndex(0);
    setCurrentPage(0);
  };

  // Function to update the current page from a child component
  const updateCurrentPage = (newValue: number) => {
    setCurrentPage(newValue);
  };
  /*
  After discussion with Dai, we determined the best approach is follows:
  - fetch all property group configs in one go, store them in state
  - On the resident and/or applications page(or whenever) the resent level config is needed, just do a lookup for that property group
  - means we have to modify the DTO a bit to add PropertyGroupUuid to the resident/application dto.
  */

  return (
    <Grid item xs={12} sx={{ mt: 3.5 }}>
      <Card
        sx={{
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          height: 'calc( 100vh - 144px )',
          pb: '64px',
        }}
        className={classes.gridContainer}
        style={{ position: 'relative' }}
      >
        <Fragment>
          <ResidentsTableHeader rangeSize={rangeSize} startIndex={recordStartIndex} sortColumn={selectedSortingColumn} sortDirection={selectedSortingDirection} updateCurrentPage={updateCurrentPage} />
          <Divider sx={{ marginTop: 2 }} />
          {String(residentState.errorMessage.statusCode)[0] === '4' ? (
            <Grid container direction="row" spacing={2} alignItems="center" justifyContent="center" sx={{ height: '100vh' }}>
              <Grid item display="flex" flexDirection="column" alignItems="center">
                <Grid item display="flex" justifyContent="center" pb={3} mt={3}>
                  <img src={BadRequestIcon} alt="400" />
                </Grid>
                <Grid item justifyContent="center">
                  <Typography
                    sx={{
                      fontFamily: 'Work Sans',
                      fontStyle: 'normal',
                      fontWeight: 700,
                      fontSize: '40px',
                      lineHeight: '23px',
                      color: '#2E82AF',
                      textAlign: 'center',
                      marginTop: '10px',
                    }}
                  >
                    {residentState.errorMessage.statusCode}
                  </Typography>
                </Grid>
                <Grid mt={2} item justifyContent="center">
                  <Typography
                    sx={{
                      fontFamily: 'Work Sans',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      fontSize: '20px',
                      lineHeight: '23px',
                      color: '#9E9E9E',
                      textAlign: 'center',
                      marginTop: '10px',
                    }}
                  >
                    We are unable to load the information.
                  </Typography>
                </Grid>
                <Grid item justifyContent="center" mt={2} xs={8}>
                  <Typography
                    sx={{
                      fontFamily: 'Work Sans',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      fontSize: '14px',
                      lineHeight: '23px',
                      color: '#9E9E9E',
                      textAlign: 'center',
                      marginTop: '10px',
                    }}
                  >
                    {residentState.errorMessage.message?.length === 0 ? '' : residentState.errorMessage.message}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          ) : String(residentState.errorMessage.statusCode)[0] === '5' || isFetchingAllPropertyGroupConfigsFailed ? (
            <Grid container direction="row" spacing={2} alignItems="center" justifyContent="center" sx={{ height: '100vh' }}>
              <Grid item display="flex" flexDirection="column" alignItems="center">
                <Grid item display="flex" justifyContent="center" sx={{ marginLeft: '5%' }}>
                  <img src={Error500} alt="500" style={{ height: '35vh' }} />
                </Grid>
                <Grid item justifyContent="center">
                  <Typography
                    sx={{
                      fontFamily: 'Work Sans',
                      fontStyle: 'normal',
                      fontWeight: 700,
                      fontSize: '40px',
                      lineHeight: '23px',
                      color: '#2E82AF',
                      textAlign: 'center',
                      marginTop: '10px',
                    }}
                  >
                    {residentState.errorMessage.statusCode}
                  </Typography>
                </Grid>
                <Grid mt={2} item justifyContent="center">
                  <Typography
                    sx={{
                      fontFamily: 'Work Sans',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      fontSize: '20px',
                      lineHeight: '23px',
                      color: '#9E9E9E',
                      textAlign: 'center',
                      marginTop: '10px',
                    }}
                  >
                    There was an error processing your request.
                  </Typography>
                </Grid>
                <Grid item justifyContent="center" mt={2} xs={8}>
                  <Typography
                    sx={{
                      fontFamily: 'Work Sans',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      fontSize: '14px',
                      lineHeight: '23px',
                      color: '#9E9E9E',
                      textAlign: 'center',
                      marginTop: '10px',
                    }}
                  >
                    {residentState.errorMessage.message?.length === 0 ? '' : residentState.errorMessage.message}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          ) : (residentState.isFetchingResidents || isFetchingAllPropertyGroupConfigs) && !residentState.IsSilentReload ? (
            <Stack
              justifyContent="center"
              alignItems="center"
              style={{
                height: '1100px',
              }}
            >
              <SpinnerWithNoConfirmation />
            </Stack>
          ) : (
            <TableContainer style={{ flex: 1 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {/* Name Column */}
                    <TableCell id="subTitle" sx={{ cursor: 'pointer' }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <TableSortLabel
                          active={selectedSortingColumn === ResidentSortColumnEnum.Name}
                          direction={selectedSortingDirection === ResidentSortDirectionEnum.Asc ? 'asc' : 'desc'}
                          onClick={() => handleSort(ResidentSortColumnEnum.Name)}
                        >
                          Name
                        </TableSortLabel>
                      </Stack>
                    </TableCell>

                    {/* Resident ID Column */}
                    <TableCell id="subTitle" sx={{ cursor: 'pointer' }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <TableSortLabel
                          active={selectedSortingColumn === ResidentSortColumnEnum.ResidentId}
                          direction={selectedSortingDirection === ResidentSortDirectionEnum.Asc ? 'asc' : 'desc'}
                          onClick={() => handleSort(ResidentSortColumnEnum.ResidentId)}
                        >
                          Resident ID
                        </TableSortLabel>
                      </Stack>
                    </TableCell>

                    {/* Property Column */}
                    <TableCell id="subTitle" sx={{ cursor: 'pointer' }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <TableSortLabel
                          active={selectedSortingColumn === ResidentSortColumnEnum.Property}
                          direction={selectedSortingDirection === ResidentSortDirectionEnum.Asc ? 'asc' : 'desc'}
                          onClick={() => handleSort(ResidentSortColumnEnum.Property)}
                        >
                          Property
                        </TableSortLabel>
                      </Stack>
                    </TableCell>

                    {/* Balance Column */}
                    <TableCell id="subTitle" sx={{ cursor: 'pointer' }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <TableSortLabel
                          active={selectedSortingColumn === ResidentSortColumnEnum.Balance}
                          direction={selectedSortingDirection === ResidentSortDirectionEnum.Asc ? 'asc' : 'desc'}
                          onClick={() => handleSort(ResidentSortColumnEnum.Balance)}
                        >
                          Balance
                        </TableSortLabel>
                      </Stack>
                    </TableCell>

                    <TableCell id="subTitle" sx={{ textAlign: 'center' }}>
                      <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                        Resident Portal
                      </Stack>
                    </TableCell>
                    <TableCell id="subTitle">
                      <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                        Registration
                      </Stack>
                    </TableCell>
                    <TableCell id="subTitle">
                      <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                        Edit
                      </Stack>
                    </TableCell>
                    <TableCell id="subTitle">
                      <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                        Accepted Payments
                      </Stack>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody style={{ left: '28px' }}>
                  {residentPages?.[currentPage]?.Rows?.length ? (
                    residentPages?.[currentPage]?.Rows?.map((resident) => {
                      const isAtLeastOneRegisteredUser = !!resident.UserProfiles?.length;
                      let amount = null;
                      if (resident?.Lease?.Balance) amount = resident?.Lease?.Balance / 100;
                      const propGroupConfigs = allPropertyGroupsAssignedConfigs?.find((pgc) => pgc.Uuid === resident.PropertyGroupUuid) || ({} as AssignedConfigsDto);
                      let propGroupLevelAllowsPartialPayToday = false;
                      if (propGroupConfigs) {
                        propGroupLevelAllowsPartialPayToday = isActionAllowedInRange(
                          propGroupConfigs?.PartialPaymentsNotBeforeDay || 32, //*we can do better th an this.
                          propGroupConfigs?.PartialPaymentsNotAfterDay || 0,
                          new Date().getDate(),
                        );
                      }
                      return (
                        <Fragment key={resident.Uuid}>
                          <TableRow>
                            <TableCell id="content">{`${resident?.FirstName || ''} ${resident?.MiddleName || ''} ${resident?.LastName || ''}`}</TableCell>
                            <TableCell id="content">{resident?.ExternalRefId2 || ''}</TableCell>
                            <TableCell id="content">{resident?.Lease?.PropertyName}</TableCell>
                            <TableCell id="content">
                              <NumberFormat
                                thousandsGroupStyle="thousand"
                                value={amount}
                                prefix="$"
                                decimalSeparator="."
                                displayType="text"
                                type="text"
                                thousandSeparator={true}
                                allowNegative={true}
                                decimalScale={2}
                                fixedDecimalScale={true}
                              />
                            </TableCell>
                            <TableCell id="content">
                              <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                                <Tooltip title="Login to resident portal to make payment and perform other functions." placement="right">
                                  {/* this will log pm into resident portal */}
                                  <img
                                    id="new-tab"
                                    src={ResidentPortalIcon}
                                    alt=""
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => {
                                      setSelectedResident(resident);
                                      if (resident.UserProfiles?.length === 1) {
                                        const userToImpersonate = resident.UserProfiles?.[0]?.UserUuid;
                                        window.open(`/login?logAsEntityUuid=${resident.Uuid}&logAsUserUuid=${userToImpersonate}`, '_blank');
                                      } else if (!resident.UserProfiles?.length) {
                                        window.open(`/login?logAsEntityUuid=${resident.Uuid}`, '_blank');
                                      } else {
                                        setOpenLogAsProfileUsersModal(true);
                                      }
                                    }}
                                    className="logas-res-btn"
                                  />
                                </Tooltip>
                              </Stack>
                            </TableCell>
                            <TableCell id="content">
                              <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                                <Tooltip
                                  title={isAtLeastOneRegisteredUser ? 'Reset Password' : 'Invite to Portal'}
                                  placement="right"
                                  onClick={() => {
                                    handleOpenModal(resident);
                                    setOpenUserRegistrationModalManager(true);
                                  }}
                                >
                                  <img
                                    src={isAtLeastOneRegisteredUser ? RegisteredIcon : NotRegisteredIcon}
                                    alt=""
                                    style={{ cursor: 'pointer' }}
                                    className={isAtLeastOneRegisteredUser ? 'registered' : 'not-registered'}
                                    data-e2e={resident?.Uuid}
                                  />
                                </Tooltip>
                              </Stack>
                            </TableCell>
                            <TableCell id="content">
                              <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                                <Tooltip title="Coming soon" placement="right">
                                  <img src={EditResidentIcon} alt="" style={{ cursor: 'pointer' }} />
                                </Tooltip>
                              </Stack>
                            </TableCell>
                            <TableCell id="content">
                              <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                                <Tooltip
                                  title={
                                    !resident.Lease.AcceptsPayments
                                      ? 'Restricted by integration data'
                                      : !resident.AchEnabled && !resident.CreditcardEnabled && !resident.DebitcardEnabled
                                      ? 'All available payment methods disabled.'
                                      : !resident.AchEnabled
                                      ? 'ACH is restricted.'
                                      : !resident.CreditcardEnabled || !resident.DebitcardEnabled
                                      ? 'Cards are restricted.'
                                      : 'All available payment methods enabled.'
                                  }
                                  placement="right"
                                  onClick={(e) => {
                                    handleClick(e, resident.Uuid);
                                  }}
                                >
                                  <img
                                    src={
                                      !resident.Lease.AcceptsPayments || (!resident.AchEnabled && !resident.CreditcardEnabled && !resident.DebitcardEnabled)
                                        ? RedRestrict
                                        : resident.AchEnabled && resident.CreditcardEnabled && resident.DebitcardEnabled
                                        ? BlackRestrict
                                        : resident.AchEnabled || resident.CreditcardEnabled || resident.DebitcardEnabled || resident.Lease.AllowPartialPayments
                                        ? YellowRestrict
                                        : ''
                                    }
                                    alt=""
                                    style={{ cursor: 'pointer' }}
                                  />
                                </Tooltip>
                                {selectedResidentUuid === resident.Uuid ? (
                                  <Popover
                                    open={open}
                                    anchorEl={anchorEl}
                                    onClose={handleClose}
                                    anchorOrigin={{
                                      vertical: 'bottom',
                                      horizontal: 'left',
                                    }}
                                    sx={{
                                      '& .MuiPopover-paper': {
                                        width: '150px',
                                        height: '40px',
                                      },
                                    }}
                                  >
                                    <Grid container sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
                                      {residentState.isEditingResident || residentState.isFetchingResidents ? (
                                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                          <CircularProgress size={20} />
                                        </Box>
                                      ) : (
                                        <Grid container sx={{ display: 'flex', justifyContent: 'space-evenly', pt: 1 }} spacing={2}>
                                          <Grid item>
                                            <Tooltip
                                              title={
                                                resident.CreditcardEnabled && resident.DebitcardEnabled
                                                  ? 'Click here to prevent card payments from this resident.'
                                                  : 'Click here to enable card payments for this resident.'
                                              }
                                              placement="bottom-start"
                                            >
                                              <img
                                                src={resident.CreditcardEnabled && resident.DebitcardEnabled ? BlackCard : RedCard}
                                                alt=""
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => {
                                                  dispatch({
                                                    type: getEditResidentData.type,
                                                    payload: {
                                                      residentUuid: selectedResidentUuid,
                                                      residentPaymentTypeField: PaymentTypeField.Card,
                                                      residentPaymentTypeValue: resident.CreditcardEnabled || resident.DebitcardEnabled ? 0 : 1,
                                                    } as ResidentEditParamsDTO,
                                                  });
                                                }}
                                              />
                                            </Tooltip>
                                          </Grid>
                                          <Grid item>
                                            <Tooltip
                                              title={resident.AchEnabled ? 'Click here to prevent ACH payments from this resident.' : 'Click here to enable ACH payments for this resident.'}
                                              placement="bottom-start"
                                            >
                                              <img
                                                src={resident.AchEnabled ? BlackAch : RedAch}
                                                alt=""
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => {
                                                  dispatch({
                                                    type: getEditResidentData.type,
                                                    payload: {
                                                      residentUuid: selectedResidentUuid,
                                                      residentPaymentTypeField: PaymentTypeField.Ach,
                                                      residentPaymentTypeValue: resident.AchEnabled ? 0 : 1,
                                                    } as ResidentEditParamsDTO,
                                                  });
                                                }}
                                              />
                                            </Tooltip>
                                          </Grid>
                                          <Grid item>
                                            <Tooltip
                                              title={
                                                (resident.Lease.AllowPartialPayments === null ? propGroupLevelAllowsPartialPayToday : resident.Lease.AllowPartialPayments)
                                                  ? 'Click here to prevent partial payments from all residents on this lease.'
                                                  : 'Click here to allow partial payments for all residents on this lease.'
                                              }
                                              placement="bottom-start"
                                            >
                                              <img
                                                src={
                                                  (resident.Lease.AllowPartialPayments === null ? propGroupLevelAllowsPartialPayToday : resident.Lease.AllowPartialPayments) ? BlackPartial : RedPartial
                                                }
                                                alt=""
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => {
                                                  let newValue: number;
                                                  if (resident.Lease.AllowPartialPayments === null) newValue = propGroupLevelAllowsPartialPayToday ? 0 : 1;
                                                  else newValue = resident.Lease.AllowPartialPayments ? 0 : 1;

                                                  dispatch({
                                                    type: getEditResidentData.type,
                                                    payload: {
                                                      residentUuid: selectedResidentUuid,
                                                      residentPaymentTypeField: PaymentTypeField.LeasePartialPayment,
                                                      residentPaymentTypeValue: newValue,
                                                    } as ResidentEditParamsDTO,
                                                  });
                                                }}
                                              />
                                            </Tooltip>
                                          </Grid>
                                        </Grid>
                                      )}
                                    </Grid>
                                  </Popover>
                                ) : (
                                  <></>
                                )}
                              </Stack>
                            </TableCell>
                          </TableRow>
                        </Fragment>
                      );
                    })
                  ) : (
                    <></>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <TablePagination
            component="div"
            rowsPerPageOptions={[10, 50, 100, 200]}
            count={residentResponse.TotalCount}
            page={currentPage}
            onPageChange={handleChangePage}
            rowsPerPage={rangeSize}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ position: 'absolute', bottom: 10, right: 10 }}
          />

          {/* adhocOTPDialog */}
          {<MakeAdhocOneTimePayment showAdhocOTPDialog={showAdhocOTPDialog} setShowAdhocOTPDialog={setShowAdhocOTPDialog} residentsDetails={selectedResident} />}
          <UserRegistrationModalManager
            open={openUserRegistrationModalManager}
            profileUsers={selectedResident?.UserProfiles || []}
            profileInvitedUsers={selectedResident?.InvitedProfiles || []}
            onClose={() => {
              setSelectedResident(null);
              setOpenUserRegistrationModalManager(false);
            }}
            profileDetails={
              {
                profileUuid: selectedResident?.Uuid,
                profileRole: Role.Resident,
                profileIntegrationEmail: selectedResident?.IntegrationEmail,
                profileIntegrationFirstName: selectedResident?.FirstName,
                profileIntegrationLastName: selectedResident?.LastName,
                profileIntegrationMiddleName: selectedResident?.MiddleName,
              } as UserRegistrationProfileDetails
            }
          />
          <LogAsProfileUserModal
            open={openLogAsProfileUsersModal}
            onClose={() => {
              setOpenLogAsProfileUsersModal(false);
              setSelectedResident(null);
            }}
            profileUsers={selectedResident?.UserProfiles || []}
            profileUuid={selectedResident?.Uuid || ''}
          />
        </Fragment>
      </Card>
    </Grid>
  );
}
